/**
 * AccountPage — the post-checkout landing page AND the standalone
 * purchase/account dashboard.
 *
 * Two modes, distinguished by URL query params:
 *
 *   1. Post-checkout: `?upgrade=success&provider=dodo|stripe[&session_id=...]`
 *      Refreshes auth state (loadMe), shows a celebratory Pro-activated
 *      banner, then renders the standard Pro dashboard below.
 *      Pre-launch this is also Dodo's reviewer-facing "success" page —
 *      they will land here after the sandbox checkout walkthrough.
 *
 *   2. Standalone: just `/account` — direct entry from the user menu.
 *      Shows the same Pro dashboard without the success banner.
 *
 * Curvle Pro is a one-time lifetime unlock — no auto-renewal, no
 * subscription to cancel. The dashboard surfaces the purchase date, a
 * "view receipts" portal link, and a 7-day refund button when eligible.
 *
 * Why a real page (not just a modal): merchant-review and email receipts
 * point recipients at a stable URL. A modal-only flow would route back to
 * the homepage after redirect, which fails the "land somewhere meaningful
 * after payment" expectation of every merchant acceptance review.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, ApiError, type BillingStatus } from "@/api/client";
import { useAuthStore } from "@/store/authStore";
import { flagUrl } from "@/lib/countries";

function formatPrice(cents: number, currency: string = "USD"): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  } catch {
    return `${currency} ${(cents / 100).toFixed(2)}`;
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export function AccountPage() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const status = useAuthStore((s) => s.status);
  const loadMe = useAuthStore((s) => s.loadMe);

  const [searchParams, setSearchParams] = useSearchParams();
  const upgradeFlag = searchParams.get("upgrade");
  const provider = searchParams.get("provider");

  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [refreshAttempts, setRefreshAttempts] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // On mount: if upgrade=success, refresh auth state immediately. Webhooks
  // are usually <1s but can lag — re-poll up to 3 times with backoff so the
  // user sees the Pro state without manually refreshing.
  useEffect(() => {
    if (upgradeFlag === "success") {
      void loadMe();
    }
  }, [upgradeFlag, loadMe]);

  // Re-poll loadMe a few times if user is signed-in but not yet Pro AFTER a
  // success redirect. Catches the brief window before the webhook lands.
  useEffect(() => {
    if (upgradeFlag !== "success") return;
    if (status !== "authed") return;
    if (user?.isPro) return;
    if (refreshAttempts >= 3) return;
    const t = window.setTimeout(() => {
      void loadMe().then(() => setRefreshAttempts((n) => n + 1));
    }, 1500 + refreshAttempts * 1500);
    return () => window.clearTimeout(t);
  }, [upgradeFlag, status, user?.isPro, refreshAttempts, loadMe]);

  // Fetch detailed billing status (provider, period, tier).
  useEffect(() => {
    if (!user?.email) {
      setBilling(null);
      return;
    }
    api.billingStatus().then(setBilling).catch(() => setBilling(null));
  }, [user?.email, user?.isPro]);

  // Auto-clear toast.
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 2400);
    return () => window.clearTimeout(t);
  }, [toast]);

  // No onCancel — Curvle Pro is a one-time lifetime unlock, there's
  // nothing to cancel. The 7-day refund button below is the only path
  // back out of Pro.

  const onRefund = async () => {
    if (busy) return;
    const ok = window.confirm(
      "Request a full refund? Your Pro access ends immediately. The refund posts to your card in 5–10 business days.",
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await api.requestRefund();
      await loadMe();
      setToast("Refund issued.");
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        setError(
          "Outside the 7-day auto-refund window, or a prior refund exists. Email vbraju@thoughtplanet.com for a manual review.",
        );
      } else {
        setError(describeError(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const dismissSuccess = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("upgrade");
    next.delete("provider");
    next.delete("session_id");
    setSearchParams(next, { replace: true });
  };

  // -- Render ----------------------------------------------------------------

  if (status === "unknown") {
    return (
      <Shell>
        <p className="text-sm opacity-70">{t("common.loading")}</p>
      </Shell>
    );
  }

  if (status === "anonymous" || !user) {
    return (
      <Shell>
        <h1 className="text-2xl font-bold">{t("account.title")}</h1>
        <p className="mt-3 text-sm opacity-80">
          {t("account.not_signed_in")}
        </p>
        <Link
          to="/"
          className="mt-4 inline-block rounded-full bg-white/10 px-4 py-2 text-sm font-semibold hover:bg-white/20"
        >
          {t("account.back_home")}
        </Link>
      </Shell>
    );
  }

  const showSuccess = upgradeFlag === "success";
  // Stripe is not exposed in UI as of May 22 2026; legacy Stripe-paid users
  // still exist server-side and the post-checkout banner may still be hit
  // with `?provider=stripe` from an older session, so we fall back to a
  // brand-agnostic label rather than printing "Stripe" anywhere.
  const providerLabel =
    provider === "gumroad"
      ? "Gumroad"
      : provider === "dodo"
      ? "Dodo Payments"
      : "your card";

  return (
    <Shell>
      {/* Post-checkout success banner */}
      {showSuccess && (
        <div className="mb-6 rounded-2xl bg-emerald-500/15 p-5 ring-1 ring-emerald-300/40">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-lg font-bold text-emerald-200">
                {user.isPro ? t("account.welcome_pro") : t("account.payment_processing")}
              </p>
              <p className="mt-1 text-sm opacity-80">
                {user.isPro
                  ? `Your Pro features are active. Charged via ${providerLabel}. A receipt is on its way to ${user.email}.`
                  : `We're activating your Pro access — this usually takes a few seconds. Charged via ${providerLabel}.`}
              </p>
              {!user.isPro && refreshAttempts >= 3 && (
                <p className="mt-2 text-xs text-amber-200">
                  Still waiting for the payment-provider confirmation. Refresh in a minute, or email
                  {" "}<a className="underline" href="mailto:vbraju@thoughtplanet.com">vbraju@thoughtplanet.com</a>{" "}
                  if it doesn&rsquo;t flip within 10 minutes.
                </p>
              )}
            </div>
            <button
              type="button"
              aria-label="Dismiss"
              className="text-emerald-200/60 hover:text-emerald-100"
              onClick={dismissSuccess}
            >
              ✕
            </button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to="/play"
              className="rounded-full bg-amber-300 px-4 py-2 text-sm font-bold text-emerald-950 hover:bg-amber-200"
            >
              ▶ {t("account.today_kick")}
            </Link>
            <Link
              to="/leaderboard"
              className="rounded-full bg-white/10 px-4 py-2 text-sm font-semibold hover:bg-white/20"
            >
              View leaderboard
            </Link>
          </div>
        </div>
      )}

      <h1 className="text-2xl font-bold">{t("account.title")}</h1>

      {/* Profile card */}
      <div className="mt-4 rounded-2xl bg-white/5 p-5 ring-1 ring-white/10">
        <div className="flex items-center gap-4">
          {flagUrl(user.countryCode, 80) ? (
            <img
              src={flagUrl(user.countryCode, 80)!}
              width={40}
              height={30}
              alt=""
              className="rounded-sm shadow-[0_0_0_1px_rgba(255,255,255,0.15)]"
            />
          ) : (
            <span aria-hidden="true" className="text-2xl">🌐</span>
          )}
          <div className="flex-1 min-w-0">
            <p className="truncate text-base font-semibold">
              {user.displayName || user.email || "Curvle player"}
            </p>
            <p className="truncate text-xs opacity-60">{user.email}</p>
          </div>
          {user.isPro && (
            <span className="rounded-full bg-amber-300/90 px-2 py-1 text-[10px] font-bold uppercase text-amber-950">
              Pro
            </span>
          )}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 text-xs opacity-80">
          <div>
            <p className="opacity-60">{t("account.current_streak")}</p>
            <p className="text-base font-semibold">{user.currentStreak}</p>
          </div>
          <div>
            <p className="opacity-60">{t("account.longest_streak")}</p>
            <p className="text-base font-semibold">{user.longestStreak}</p>
          </div>
        </div>
      </div>

      {/* Curvle Pro card (one-time lifetime unlock — no recurring billing) */}
      <div className="mt-4 rounded-2xl bg-white/5 p-5 ring-1 ring-white/10">
        <h2 className="text-lg font-semibold">{t("account.subscription_title")}</h2>
        {user.isPro ? (
          <>
            <p className="mt-2 text-sm">
              <span className="font-semibold text-amber-200">Curvle Pro</span>
              {user.founderNumber != null ? (
                <>
                  {" · "}
                  <span className="rounded-full bg-amber-400 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-950">
                    {t("founder.account_self_label", { number: user.founderNumber })}
                  </span>{" "}
                  · {t("founder.account_self_suffix")}
                </>
              ) : (
                <>
                  {" · "}lifetime access · purchased{" "}
                  <strong>{formatDate(user.proSince)}</strong>
                </>
              )}
            </p>
            {billing && user.founderNumber == null && (
              <p className="mt-1 text-xs opacity-60">
                {formatPrice(billing.activePriceCents)} one-time
                {billing.paymentProvider === "gumroad" && " · via Gumroad"}
                {billing.paymentProvider === "dodo" && " · via Dodo"}
                {billing.activeTier === "wc_special" && " · World Cup launch pricing"}
              </p>
            )}
            <div className="mt-4 flex flex-col gap-2">
              {billing?.paymentProvider === "gumroad" && (
                <p className="rounded-full bg-white/5 px-4 py-2 text-center text-xs opacity-70">
                  Your receipt &amp; invoice are in your email from Gumroad.
                </p>
              )}
              {user.founderNumber != null && (
                <p className="rounded-full bg-amber-300/10 px-4 py-2 text-center text-xs text-amber-100 ring-1 ring-amber-300/20">
                  {t("founder.account_thank_you")}
                </p>
              )}
              {/* No cancel button — Curvle Pro is a one-time lifetime
                  unlock. The refund button below is the only way to
                  revoke Pro, and only within 7 days of purchase. */}
              {billing?.paymentProvider === "gumroad" &&
                user.proSince &&
                Date.now() - new Date(user.proSince).getTime() < 7 * 24 * 60 * 60 * 1000 && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={onRefund}
                    className="rounded-full bg-white/5 px-4 py-2 text-xs opacity-70 hover:opacity-100 disabled:opacity-30"
                  >
                    Request refund (within 7-day window)
                  </button>
                )}
            </div>
            {error && <p className="mt-3 text-sm text-rose-300">{error}</p>}
            {toast && <p className="mt-3 text-sm text-emerald-300">{toast}</p>}
          </>
        ) : (
          <>
            <p className="mt-2 text-sm opacity-80">
              {t("account.free_tier_body")}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link
                to="/pricing"
                className="rounded-full bg-amber-300 px-4 py-2 text-sm font-bold text-emerald-950 hover:bg-amber-200"
              >
                {t("account.see_pro")}
              </Link>
              <Link
                to="/play"
                className="rounded-full bg-white/10 px-4 py-2 text-sm font-semibold hover:bg-white/20"
              >
                {t("account.today_kick")}
              </Link>
            </div>
          </>
        )}
      </div>

      {/* Legal & support links */}
      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs opacity-60">
        <Link to="/terms" className="hover:opacity-100 hover:underline">Terms</Link>
        <Link to="/privacy" className="hover:opacity-100 hover:underline">Privacy</Link>
        <Link to="/refund" className="hover:opacity-100 hover:underline">Refund policy</Link>
        <a className="hover:opacity-100 hover:underline" href="mailto:vbraju@thoughtplanet.com">Support</a>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-emerald-950 text-white">
      <header className="border-b border-white/10 px-4 py-3">
        <div className="mx-auto flex max-w-2xl items-center justify-between">
          <Link to="/" className="text-sm font-bold tracking-wider opacity-80 hover:opacity-100">
            CURVLE
          </Link>
          <Link
            to="/play"
            className="rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold hover:bg-white/20"
          >
            Today&rsquo;s kick →
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-2xl px-4 py-6">{children}</main>
    </div>
  );
}

function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return "Please sign in first.";
    if (err.status === 429) return "Too many requests — try again in a minute.";
    return err.message || "Something went wrong.";
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong.";
}
